// UI Components
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";

// Core Dependencies
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect, Suspense, lazy } from "react";
import { initSentry } from "@/utils/sentry";
import { hideAxeptioButtonAfterConsent } from "@/utils/axeptio";

// Context and Utilities
import { AuthProvider } from "@/contexts/AuthContext";
import { SettingsProvider } from "@/contexts/SettingsContext";
import ScrollToTop from "@/components/utils/ScrollToTop";
import AnalyticsPageViewTracker from "@/components/utils/AnalyticsPageViewTracker";
import TikTokPageViewTracker from "@/components/utils/TikTokPageViewTracker";
import GroupVoteStickyIndicator from "@/components/layout/GroupVoteStickyIndicator";
import ErrorBoundary from "@/components/utils/ErrorBoundary";
import { SentryErrorBoundary } from "@/components/utils/SentryErrorBoundary";
import PageLoader from "@/components/utils/PageLoader";

// Critical pages - loaded immediately (homepage, search, error page)
import Index from "@/pages/Index";
import Search from "@/pages/Search";
import NotFound from "@/pages/NotFound";

// Lazy-loaded pages - loaded on demand for better performance
const ActivityDetail = lazy(() => import("@/pages/ActivityDetail"));
const Booking = lazy(() => import("@/pages/Booking"));
const Payment = lazy(() => import("@/pages/Payment"));
const BookingSuccess = lazy(() => import("@/pages/BookingSuccess"));
const UserAccount = lazy(() => import("@/pages/UserAccount"));
const Auth = lazy(() => import("@/pages/Auth"));
const About = lazy(() => import("@/pages/About"));
const Contact = lazy(() => import("@/pages/Contact"));
const Pro = lazy(() => import("@/pages/Pro"));
const ProDashboard = lazy(() => import("@/pages/ProDashboard"));
const ProActivityPreview = lazy(() => import("@/pages/ProActivityPreview"));
const ProOnboarding = lazy(() => import("@/pages/ProOnboarding"));
const VerificationSuccess = lazy(() => import("@/pages/VerificationSuccess"));
const VerificationPending = lazy(() => import("@/pages/VerificationPending"));
const SetPassword = lazy(() => import("@/pages/SetPassword"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const AdminDataDashboard = lazy(() => import("@/pages/AdminDataDashboard"));
const AdminProfessionals = lazy(() => import("@/pages/AdminProfessionals"));
const AdminCreateProfessional = lazy(() => import("@/pages/AdminCreateProfessional"));
const AdminSettings = lazy(() => import("@/pages/AdminSettings"));
const AdminProfessionalAvailability = lazy(() => import("@/pages/AdminProfessionalAvailability"));
const GroupVote = lazy(() => import("@/pages/GroupVote"));
const MentionsLegales = lazy(() => import("@/pages/MentionsLegales"));
const Confidentialite = lazy(() => import("@/pages/Confidentialite"));
const CGV = lazy(() => import("@/pages/CGV"));
const CGUPro = lazy(() => import("@/pages/CGUPro"));
const Cookies = lazy(() => import("@/pages/Cookies"));
const FAQ = lazy(() => import("@/pages/FAQ"));
const CategoryLandingPage = lazy(() => import("@/pages/CategoryLandingPage"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const VenuePage = lazy(() => import("@/pages/VenuePage"));

// Create a new QueryClient instance that will remain stable across renders
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

const App = () => {
  useEffect(() => {
    initSentry();
    hideAxeptioButtonAfterConsent();
  }, []);

  return (
    <ErrorBoundary>
      <SentryErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <SettingsProvider>
              <TooltipProvider>
                <Toaster />
                <Sonner />
                <BrowserRouter>
                  <ScrollToTop />
                  <AnalyticsPageViewTracker />
                  <TikTokPageViewTracker />
                  <GroupVoteStickyIndicator />
                  <Routes>
                    {/* Critical routes - loaded immediately */}
                    <Route path="/" element={<Index />} />
                    <Route path="/search" element={<Search />} />
                    
                    {/* Lazy-loaded routes with Suspense and Error Boundaries */}
                    {/* Map page disabled until more venues — redirect to search */}
                    <Route path="/map" element={<Navigate to="/search" replace />} />
                    <Route
                      path="/activity/:id"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <ActivityDetail />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/venue/:id"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <VenuePage />
                        </Suspense>
                      }
                    />
                    {/* Wrap critical booking flow with error boundary */}
                    <Route 
                      path="/booking/:id" 
                      element={
                        <SentryErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <Booking />
                          </Suspense>
                        </SentryErrorBoundary>
                      } 
                    />
                    <Route 
                      path="/booking/pack/:packId" 
                      element={
                        <SentryErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <Booking />
                          </Suspense>
                        </SentryErrorBoundary>
                      } 
                    />
                    <Route 
                      path="/payment/:bookingId" 
                      element={
                        <SentryErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <Payment />
                          </Suspense>
                        </SentryErrorBoundary>
                      } 
                    />
                    <Route 
                      path="/booking-success/:bookingId" 
                      element={
                        <SentryErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <BookingSuccess />
                          </Suspense>
                        </SentryErrorBoundary>
                      } 
                    />
                    <Route 
                      path="/group-vote/:voteId" 
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <GroupVote />
                        </Suspense>
                      } 
                    />
                    <Route 
                      path="/account" 
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <UserAccount />
                        </Suspense>
                      } 
                    />
                    {/* Wrap professional routes with error boundary */}
                    <Route 
                      path="/pro-dashboard/activity/:id" 
                      element={
                        <SentryErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <ProActivityPreview />
                          </Suspense>
                        </SentryErrorBoundary>
                      } 
                    />
                    <Route 
                      path="/pro-dashboard" 
                      element={
                        <SentryErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <ProDashboard />
                          </Suspense>
                        </SentryErrorBoundary>
                      } 
                    />
                    <Route 
                      path="/pro-onboarding" 
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <ProOnboarding />
                        </Suspense>
                      } 
                    />
                    <Route 
                      path="/auth" 
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <Auth />
                        </Suspense>
                      } 
                    />
                    <Route 
                      path="/auth/verification-pending" 
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <VerificationPending />
                        </Suspense>
                      } 
                    />
                    <Route 
                      path="/auth/verification-success" 
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <VerificationSuccess />
                        </Suspense>
                      } 
                    />
                    <Route 
                      path="/auth/forgot-password" 
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <Auth />
                        </Suspense>
                      } 
                    />
                    <Route 
                      path="/auth/reset-password" 
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <Auth />
                        </Suspense>
                      } 
                    />
                    <Route 
                      path="/about" 
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <About />
                        </Suspense>
                      } 
                    />
                    <Route 
                      path="/contact" 
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <Contact />
                        </Suspense>
                      } 
                    />
                    <Route 
                      path="/faq" 
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <FAQ />
                        </Suspense>
                      } 
                    />
                    <Route 
                      path="/pro" 
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <Pro />
                        </Suspense>
                      } 
                    />
                    <Route 
                      path="/set-password" 
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <SetPassword />
                        </Suspense>
                      } 
                    />
                    <Route 
                      path="/mentions-legales" 
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <MentionsLegales />
                        </Suspense>
                      } 
                    />
                    <Route 
                      path="/confidentialite" 
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <Confidentialite />
                        </Suspense>
                      } 
                    />
                    <Route 
                      path="/cgv" 
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <CGV />
                        </Suspense>
                      } 
                    />
                    <Route 
                      path="/cgu-pro" 
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <CGUPro />
                        </Suspense>
                      } 
                    />
                    <Route 
                      path="/cookies" 
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <Cookies />
                        </Suspense>
                      } 
                    />
                    <Route 
                      path="/terms" 
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <CGV />
                        </Suspense>
                      } 
                    />
                    <Route 
                      path="/privacy" 
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <Confidentialite />
                        </Suspense>
                      } 
                    />
                    {/* Routes d'administration - wrapped with error boundary */}
                    <Route 
                      path="/admin" 
                      element={
                        <SentryErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <AdminDashboard />
                          </Suspense>
                        </SentryErrorBoundary>
                      } 
                    />
                    <Route 
                      path="/admin/dashboard" 
                      element={
                        <SentryErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <AdminDataDashboard />
                          </Suspense>
                        </SentryErrorBoundary>
                      } 
                    />
                    <Route 
                      path="/admin/professionals" 
                      element={
                        <SentryErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <AdminProfessionals />
                          </Suspense>
                        </SentryErrorBoundary>
                      } 
                    />
                    <Route 
                      path="/admin/create-professional" 
                      element={
                        <SentryErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <AdminCreateProfessional />
                          </Suspense>
                        </SentryErrorBoundary>
                      } 
                    />
                    <Route 
                      path="/admin/settings" 
                      element={
                        <SentryErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <AdminSettings />
                          </Suspense>
                        </SentryErrorBoundary>
                      } 
                    />
                    <Route 
                      path="/admin/professionals/:professionalId/availability" 
                      element={
                        <SentryErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <AdminProfessionalAvailability />
                          </Suspense>
                        </SentryErrorBoundary>
                      } 
                    />
                    {/* SEO category landing pages — dynamic /:slug route, filtered by known category slugs.
                        All explicit routes above have higher specificity and will match first. */}
                    <Route
                      path="/:slug"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <CategoryLandingPage />
                        </Suspense>
                      }
                    />
                    {/* Blog */}
                    <Route
                      path="/blog"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <Blog />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/blog/:slug"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <BlogPost />
                        </Suspense>
                      }
                    />
                    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </BrowserRouter>
              </TooltipProvider>
            </SettingsProvider>
          </AuthProvider>
        </QueryClientProvider>
      </SentryErrorBoundary>
    </ErrorBoundary>
  );
};

export default App;